<template>
  <div>
    <ds-heading text="Exportar Dados" />
    <ds-button
      class="ds-u-margin-bottom"
      theme="primary"
      @click="openExportationRollover">
      Exportar Dados
    </ds-button>
    <ds-page>
      <ds-data-grid
        ref="dataGrid"
        :columns="columns"
        :fetch-action="fetchAction"
        :pagination="pagination"
        :advanced-filters="advancedFilters"
        :batch-actions="batchActions"
        is-collapse
        hide-collapse-actions
        search-term-placeholder="Pesquisar"
        show-actions
        show-search-term-filter
        @fetch-success="onFetchSuccess">
        <ds-data-grid-tr
          v-for="backupRequest in backupRequests"
          :key="backupRequest.id"
          :item="backupRequest"
          is-collapse
          initial-collapsed
          show-empty-rows
          :custom-actions="customActions">
          <ds-td>
            {{ backupRequest.employerName }}
          </ds-td>
          <ds-td>
            {{ backupRequest.requestDate }}
          </ds-td>
          <ds-td>
            {{ backupRequest.period }}
          </ds-td>
          <ds-td>
            <ds-badge :theme="getStatusBadgeTheme(backupRequest.status)">
              {{ getStatusBadgeText(backupRequest.status) }}
            </ds-badge>
          </ds-td>
          <template slot="nested">
            <ds-data-grid-tr
              v-for="report in backupRequest.reports"
              :key="report.name"
              :item="report"
              is-nested
              disable-all-actions>
              <ds-td colspan="4"> {{ report.description }}</ds-td>
            </ds-data-grid-tr>
          </template>
        </ds-data-grid-tr>
      </ds-data-grid>
    </ds-page>
  </div>
</template>
<script>
import * as websocketService from 'packages/common/websocket/websocketService';
import { confirmService, toasterService } from 'packages/common/ds-components';
import {
  downloadBackupRequests,
  removeBackupRequests,
  getStatuses,
  getBackupRequests,
  getEmployers,
  getStatusBadgeTheme,
  getStatusBadgeText,
  isBlockedToDownloadBackupRequest,
  isBlockedToRemoveBackupRequest,
} from './backupRequestService';
import BackupRequestRollover from './components/BackupRequestRollover';

export default {
  name: 'BackupRequestPage',
  data() {
    return {
      singleRemovalTitle: 'Item removido!',
      singleRemovalMessage: 'A exportação foi removida com sucesso',
      singleRemovalErrorTitle: 'Não foi possível remover a exportação!',
      singleRemovalErrorMessage: 'Ocorreu um erro ao remover a exportação',
      batchRemovalTitle: 'Itens removidos!',
      batchRemovalMessage: 'Os itens selecionados foram removidos com successo',
      batchRemovalErrorTitle: 'Não foi possível remover os itens!',
      batchRemovalErrorMessage: 'Ocorreu um erro ao remover os itens selecionados',
      columns: [
        { name: 'Nome da empresa', key: 'employerName' },
        { name: 'Data da exportação', key: 'requestDate' },
        {
          name: 'Período exportado', key: 'exportationPeriod', sortable: false,
        },
        { name: 'Situação do processamento', key: 'backupRequestStatus' },
      ],
      pagination: {},
      backupRequests: [],
      advancedFilters: {
        employer: {
          name: 'Empresa',
          type: 'select',
          onFetch: () => this.getEmployers(),
          onQuery: term => this.getEmployers(term),
        },
        date: {
          name: 'Período exportado',
          type: 'period',
        },
        status: {
          name: 'Situação do processamento',
          type: 'select',
          onFetch: () => this.getStatuses(),
          onQuery: term => this.getStatuses(term),
        },
      },
      batchActions: [
        {
          name: 'Baixar relatórios',
          action(_, keys) {
            return downloadBackupRequests(keys);
          },
          validationRules: [
            {
              validationRule: rows => {
                return rows.every(r => !isBlockedToDownloadBackupRequest(r));
              },
              disabledMessage: 'Selecione apenas exportações concluídas',
            },
          ],
        },
        {
          name: 'Excluir',
          action: (_, keys) => this.handleBatchRemoval(keys),
          validationRules: [
            {
              validationRule: rows => {
                return rows.every(r => !isBlockedToRemoveBackupRequest(r));
              },
              disabledMessage: 'Selecione apenas exportações concluídas ou com erro',
            },
          ],
        },
      ],
      customActions: [
        {
          name: 'Baixar relatório',
          action: ({ id }) => downloadBackupRequests([id]),
          disabled: row => {
            return isBlockedToDownloadBackupRequest(row);
          },
          disabledTooltip: 'É possível baixar apenas exportações concluídas',
        },
        {
          name: 'Excluir',
          action: item => this.removeAction(item),
          disabled: row => {
            return isBlockedToRemoveBackupRequest(row);
          },
          disabledTooltip: 'É possível excluir apenas exportações concluídas ou com erro',
        },
      ],
    };
  },
  computed: {
    websocketEvents() {
      const events = websocketService.getWebsocketEvents();
      return [events.PERSONNEL_DEPARTMENT_BACKUP_UPDATED_STATUS];
    },
  },
  destroyed() {
    this.turnOffWebsocket();
  },
  mounted() {
    this.turnOnWebsocket();
  },
  methods: {
    getStatuses,
    getEmployers,
    getStatusBadgeTheme,
    getStatusBadgeText,
    fetchAction(params) {
      return getBackupRequests(params);
    },
    onFetchSuccess({ content, totalElements }) {
      this.backupRequests = content;
      this.pagination = {
        ...this.pagination,
        totalItems: totalElements,
      };
    },
    openExportationRollover() {
      BackupRequestRollover.open({}, {
        on: {
          'submit-success': this.refreshContent,
        },
      });
    },
    removeAction({ id }) {
      return new Promise(resolve => {
        confirmService.confirm({
          title: 'Deseja excluir este item?',
          message: 'Esta ação não poderá ser desfeita',
          confirmButtonText: 'Excluir',
          onConfirm: () => this.onRemoveConfirm(id, resolve),
        });
      });
    },
    onRemoveConfirm(id, resolve) {
      removeBackupRequests([id])
        .then(() =>
          this.onRemoveSuccess(this.singleRemovalTitle, this.singleRemovalMessage))
        .catch(() => {
          this.onRemoveError(this.singleRemovalErrorTitle, this.singleRemovalErrorMessage);
        });

      resolve();
    },
    handleBatchRemoval(keys) {
      return new Promise(resolve => {
        confirmService.confirm({
          title: 'Excluir itens',
          message: 'Deseja realmente excluir todos os itens selecionados?',
          onCancel: resolve,
          onConfirm: () => this.onBatchRemovalConfirm(keys, resolve),
        });
      });
    },
    async onBatchRemovalConfirm(keys, resolve) {
      this.batchRemoveAction(keys)
        .then(() =>
          this.onRemoveSuccess(this.batchRemovalTitle, this.batchRemovalMessage))
        .catch(() =>
          this.onRemoveError(this.batchRemovalErrorTitle, this.batchRemovalErrorMessage));

      resolve();
    },
    turnOnWebsocket() {
      this.websocketEvents.forEach(event => {
        websocketService.on({ event, listener: this.onWebsocketUpdated });
      });
    },
    turnOffWebsocket() {
      this.websocketEvents.forEach(event => {
        websocketService.off({ event, listener: this.onWebsocketUpdated });
      });
    },
    onWebsocketUpdated(data) {
      const { id, status } = data;
      const backupRequestsUpdated = this.backupRequests.map(backupRequest => {
        if (backupRequest.id === id) {
          backupRequest.status = status;
        }
        return backupRequest;
      });
      this.$set(this, 'backupRequests', backupRequestsUpdated);
    },
    batchRemoveAction(ids) {
      return removeBackupRequests(ids);
    },
    onRemoveError(title, content) {
      toasterService.error({ title, content });
    },
    onRemoveSuccess(title, content) {
      toasterService.success({ title, content });
      this.refreshContent();
    },
    refreshContent() {
      if (this.$refs?.dataGrid) {
        this.$refs.dataGrid.refreshTable(true);
      }
    },
  },
};
</script>
